import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSession } from "../lib/session";
import { getCompetition, competitionField, enterPlayer, withdrawPlayer,
         enterSociety } from "../lib/api";
import { PageHead, Loading, Notice } from "../components/ui";
import Back from "../components/Back";

/**
 * Who's playing.
 *
 * "Enter everyone" suits a society where everyone plays everything. A
 * four-round order of merit has people who miss rounds, guests who play
 * one, and members who simply aren't in it.
 *
 * Entering someone after a round has opened gives them a card in it, so
 * they can't end up entered and invisible.
 */
export default function CompetitionField() {
  const { id } = useParams();
  const { isOrganiser } = useSession();

  const [comp, setComp] = useState(null);
  const [field, setField] = useState([]);
  const [ready, setReady] = useState(false);
  const [q, setQ] = useState("");
  const [show, setShow] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const [c, f] = await Promise.all([getCompetition(id), competitionField(id)]);
      setComp(c); setField(f); setReady(true);
    } catch (e) { setError(e.message); setReady(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const shown = useMemo(() => field.filter((p) => {
    if (show === "in" && !p.entered) return false;
    if (show === "out" && p.entered) return false;
    if (!q.trim()) return true;
    return p.full_name.toLowerCase().includes(q.trim().toLowerCase());
  }), [field, q, show]);

  const inCount = field.filter((p) => p.entered).length;
  const noIndex = field.filter((p) => p.current_index == null && !p.entered).length;

  async function toggle(p) {
    setError(""); setNote(""); setBusyId(p.profile_id);
    try {
      if (p.entered) {
        const what = await withdrawPlayer(id, p.profile_id);
        setNote(what === "withdrawn"
          ? `${p.full_name} withdrawn. Their cards stay on record but stop counting.`
          : `${p.full_name} taken out.`);
      } else {
        await enterPlayer(id, p.profile_id);
      }
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  }

  if (!ready) return <Loading />;
  if (!comp) return (
    <>
      <Back to="/competitions">Back</Back>
      <Notice kind="error">That competition isn't available.</Notice>
    </>
  );
  if (!isOrganiser) return <Notice kind="error">Only an organiser can change who's entered.</Notice>;

  return (
    <>
      <Back to={`/c/${id}/today`}>{comp?.name}</Back>

      <PageHead title="Who's playing"
                sub={`${inCount} of ${field.length} entered · ${comp?.name}`}>
        <button className="btn btn-ghost" disabled={busy}
                onClick={async () => {
                  setBusy(true); setError(""); setNote("");
                  try {
                    await enterSociety(id);
                    setNote("Everyone with a handicap index is in.");
                    await load();
                  } catch (e) { setError(e.message); }
                  finally { setBusy(false); }
                }}>
          {busy ? "Entering…" : "Enter everyone"}
        </button>
      </PageHead>

      <Notice kind="error">{error}</Notice>
      <Notice kind="ok">{note}</Notice>

      {noIndex > 0 && (
        <div className="notice notice-info" style={{ marginBottom: "1rem" }}>
          {noIndex} member{noIndex === 1 ? " has" : "s have"} no handicap index and
          can't be entered — their nett scores couldn't be worked out. Set one on
          the members page.
        </div>
      )}

      <div className="row" style={{ gap: ".5rem", marginBottom: ".8rem", flexWrap: "wrap" }}>
        <input className="input" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Find a member" style={{ flex: "1 1 12rem", width: "auto" }} />
        <div className="scope">
          {[["all", `All ${field.length}`], ["in", `In ${inCount}`],
            ["out", `Out ${field.length - inCount}`]].map(([k, l]) => (
            <button key={k} data-on={show === k} onClick={() => setShow(k)}>{l}</button>
          ))}
        </div>
      </div>

      <div className="list" style={{ "--cols": "minmax(0,1fr) 4rem 3.5rem 6rem" }}>
        <div className="list-row head">
          <span>Player</span>
          <span style={{ textAlign: "right" }}>Index</span>
          <span style={{ textAlign: "right" }}>Cards</span>
          <span style={{ textAlign: "right" }}>In</span>
        </div>

        {shown.map((p) => {
          const drifted = p.entered && p.frozen_index != null
            && p.current_index != null
            && Number(p.frozen_index) !== Number(p.current_index);
          return (
            <div className="list-row" key={p.profile_id}>
              <span style={{ minWidth: 0 }}>
                <strong style={{ display: "block", overflow: "hidden",
                                 textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.full_name}
                </strong>
                {p.withdrawn && <small className="muted" style={{ fontSize: ".6875rem" }}>
                  withdrawn · cards kept
                </small>}
                {drifted && <small className="muted" style={{ fontSize: ".6875rem" }}>
                  playing off {p.frozen_index}, now {p.current_index}
                </small>}
              </span>

              <span className="fig" style={{ textAlign: "right",
                    color: p.current_index == null ? "var(--red)" : undefined }}>
                {p.entered ? p.frozen_index : p.current_index ?? "—"}
              </span>

              <span className="muted" style={{ textAlign: "right", fontSize: ".8125rem" }}>
                {p.cards || "—"}
              </span>

              <span style={{ textAlign: "right" }}>
                <button className={`btn ${p.entered ? "btn-ghost" : "btn-primary"}`}
                        disabled={busyId === p.profile_id
                                  || (!p.entered && p.current_index == null)}
                        style={{ padding: ".35rem .7rem", fontSize: ".8125rem" }}
                        onClick={() => toggle(p)}>
                  {busyId === p.profile_id ? "…" : p.entered ? "Take out" : "Enter"}
                </button>
              </span>
            </div>
          );
        })}

        {shown.length === 0 && (
          <div className="list-row"><span className="muted">Nobody matches that.</span></div>
        )}
      </div>

      <p className="muted" style={{ fontSize: ".75rem", marginTop: ".7rem", lineHeight: 1.6 }}>
        A player's handicap index is frozen when they enter, so a mid-season
        change doesn't rewrite rounds they've already played. Taking someone
        out who has cards marks them withdrawn and keeps the scores — the
        standings just stop counting them.
      </p>
    </>
  );
}
